import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-hot-toast';
import { consumeLogoutReason } from '../../utils/sessionManager';
import { resumePendingDownloadAfterAuth } from '../../utils/downloadResume';
import { apiUrl } from '../../utils/apiBase';
import { CloudAuthButtons } from './cloudAuth';
import { legacyApiPath } from '../../config/environments';

function parseHashSearch(location) {
  const hash = location.hash || '';
  const qIndex = hash.indexOf('?');
  if (qIndex === -1) return new URLSearchParams(location.search || '');
  return new URLSearchParams(hash.slice(qIndex + 1));
}

function hasRepeatedComSuffix(email) {
  const domain = String(email || '').split('@').pop() || '';
  return /(?:\.com){2,}$/i.test(domain);
}

export default function LoginPage() {
  const { login, isAuthenticated, loading: authLoading, handoffPending, handoffStatus, completeIdeHandoff } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const params = useMemo(() => parseHashSearch(location), [location.hash, location.search]);
  const downloadIntent = params.get('intent') === 'download';
  const initialRegister = params.get('register') === '1';

  const [mode, setMode] = useState(initialRegister ? 'register' : 'login');
  const [email, setEmail] = useState(params.get('email') || '');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState(params.get('first_name') || '');
  const [lastName, setLastName] = useState(params.get('last_name') || '');
  const [company, setCompany] = useState('');
  const [otp, setOtp] = useState('');
  const [resetOtp, setResetOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);
  const [forgotAwaitingCode, setForgotAwaitingCode] = useState(false);

  const isTimedOut =
    params.get('timeout') === 'true' ||
    location.search.includes('timeout=true');

  useEffect(() => {
    if (!isTimedOut) return;
    const reason = consumeLogoutReason();
    const message =
      reason === 'unauthorized'
        ? 'Your session is no longer valid. Please sign in again.'
        : 'Your session expired due to inactivity. Please sign in again.';
    toast.error(message, { id: 'session-timeout-toast' });
  }, [isTimedOut]);

  useEffect(() => {
    if (downloadIntent) {
      toast('Sign in or register to download Candor IDE', { icon: '⬇️', id: 'download-intent-toast' });
    }
  }, [downloadIntent]);

  useEffect(() => {
    if (authLoading || handoffPending) return;
    if (isAuthenticated) {
      const redirectTo = location.state?.from?.pathname || '/dashboard';
      navigate(redirectTo, { replace: true });
      return;
    }

    // ***** Code Start BH25082026_1715 - Do not re-fire handoff after permanent failure
    if (handoffStatus === 'failed' || handoffStatus === 'success') return;
    // ***** Code End BH25082026_1715

    let cancelled = false;
    (async () => {
      const result = await completeIdeHandoff('login_page');
      if (cancelled) return;
      if (result.ok) {
        const redirectTo = location.state?.from?.pathname || '/dashboard';
        navigate(redirectTo, { replace: true });
      }
    })();

    return () => { cancelled = true; };
  }, [authLoading, handoffPending, handoffStatus, isAuthenticated, completeIdeHandoff, location.state, navigate]);

  if (authLoading || handoffPending) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.header}>
            <img src={`${process.env.PUBLIC_URL || ''}/candorlogo.png`} alt="Candor Logo" style={styles.logo} />
            <h1 style={styles.title}>Connecting from Candor IDE</h1>
            <p style={styles.subtitle}>Transferring your secure session to the dashboard…</p>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 4px' }}>
            <span style={styles.spinner} />
          </div>
        </div>
      </div>
    );
  }

  async function finishAuthAndRedirect() {
    const downloaded = await resumePendingDownloadAfterAuth();
    if (downloaded) {
      toast.success('Your Candor IDE download has started.');
      navigate('/', { replace: true });
      return;
    }
    const redirectTo = location.state?.from?.pathname || '/dashboard';
    navigate(redirectTo, { replace: true });
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email.trim(), password);
      await finishAuthAndRedirect();
    } catch (err) {
      setError(err.message || 'Sign in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    const trimmedEmail = email.trim().toLowerCase();
    const trimmedFirstName = firstName.trim();
    const trimmedLastName = lastName.trim();
    const trimmedCompany = company.trim();
    if (!trimmedEmail || !password || !trimmedFirstName || !trimmedLastName) {
      setError('Email, password, first name, and last name are required');
      return;
    }
    if (hasRepeatedComSuffix(trimmedEmail)) {
      setError('Please check your email address; a repeated .com suffix is not allowed.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const res = await fetch(apiUrl(process.env.REACT_APP_ENDPOINT_AUTH_REGISTER || legacyApiPath('register')), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: trimmedEmail,
          password,
          first_name: trimmedFirstName,
          last_name: trimmedLastName,
          company: trimmedCompany,
          plan_name: 'Community Edition',
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(typeof data.detail === 'string' ? data.detail : 'Registration failed.');
      }
      toast.success('Verification code sent to your email.');
      setMode('verify');
      setOtp('');
    } catch (err) {
      setError(err.message || 'Registration failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(apiUrl(process.env.REACT_APP_ENDPOINT_AUTH_FORGOT_PASSWORD || legacyApiPath('forgot_password')), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(
          typeof data.detail === 'string' ? data.detail : 'Failed to send reset code.'
        );
      }
      toast.success(
        data.message ||
          'If an account exists for this email, a verification code has been sent.'
      );
      setResetOtp('');
      setNewPassword('');
      setConfirmPassword('');
      setForgotAwaitingCode(true);
    } catch (err) {
      setError(err.message || 'Failed to send reset code.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetVerify = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    // Mirror backend validation rules client-side for instant feedback
    if (newPassword.length < 12 || newPassword.length > 34) {
      setError('Password must be 12–34 characters long.');
      return;
    }
    if (!/[A-Z]/.test(newPassword)) {
      setError('Password must contain at least one uppercase letter (A-Z).');
      return;
    }
    if (!/[a-z]/.test(newPassword)) {
      setError('Password must contain at least one lowercase letter (a-z).');
      return;
    }
    if (!/[0-9]/.test(newPassword)) {
      setError('Password must contain at least one number (0-9).');
      return;
    }
    if (!/[^A-Za-z0-9]/.test(newPassword)) {
      setError('Password must contain at least one special character (@, #, $, %, &, !, etc.).');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(apiUrl(process.env.REACT_APP_ENDPOINT_AUTH_RESET_PASSWORD || legacyApiPath('reset_password')), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          otp: resetOtp.trim(),
          newPassword: newPassword,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(
          typeof data.detail === 'string' ? data.detail : 'Invalid code or reset failed.'
        );
      }
      toast.success('Password reset successfully! Please sign in.');
      setNewPassword('');
      setConfirmPassword('');
      setResetOtp('');
      setMode('login');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(apiUrl(process.env.REACT_APP_ENDPOINT_AUTH_VERIFY_OTP || legacyApiPath('verify_otp')), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase(), otp: otp.trim() }),
      });
      if (!res.ok) throw new Error('Invalid verification code.');
      toast.success('Email verified. Signing you in…');
      await login(email.trim(), password);
      await finishAuthAndRedirect();
    } catch (err) {
      setError(err.message || 'Verification failed.');
    } finally {
      setLoading(false);
    }
  };

  const title = {
    verify: 'Verify Your Email',
    register: 'Create Your Account',
    'reset-password': 'Set New Password',
    forgot: 'Reset Your Password',
    login: 'Welcome Back',
  }[mode];

  const subtitle = {
    verify: 'Enter the 6-digit code sent to your email.',
    register: 'Start your free Candor account.',
    'reset-password': 'Enter the reset code from your email, then choose a new password.',
    forgot: "Enter your email and we'll send a reset code.",
    login: downloadIntent ? 'Sign in to download Candor IDE.' : 'Sign in to your Candor Dashboard',
  }[mode];

  return (
    <div style={styles.page}>
      <div style={styles.blob1} />
      <div style={styles.blob2} />

      <div style={styles.card}>
        <div style={styles.header}>
          <img src={`${process.env.PUBLIC_URL || ''}/candorlogo.png`} alt="Candor Logo" style={styles.logo} />
          <h1 style={styles.title}>{title}</h1>
          <p style={styles.subtitle}>{subtitle}</p>
        </div>

        {error && <div style={styles.alert}><span style={styles.alertIcon}>⚠</span>{error}</div>}

        {mode === 'login' && (
          <form onSubmit={handleLogin}>
            <CloudAuthButtons mode="login" density="compact" disabled={loading} layout="first" />
            <Field label="Work Email" id="email" value={email} onChange={setEmail} type="email" />
            <Field label="Password" id="password" type={showPwd ? 'text' : 'password'} value={password} onChange={setPassword} togglePwd={() => setShowPwd(!showPwd)} showPwd={showPwd} />
            <SubmitButton loading={loading} label="Sign In →" />
          </form>
        )}

        {mode === 'register' && (
          <form onSubmit={handleRegister}>
            <CloudAuthButtons mode="register" density="compact" disabled={loading} layout="first" />
            <div style={styles.nameRow}>
              <Field label="First Name" id="reg-first" value={firstName} onChange={setFirstName} />
              <Field label="Last Name" id="reg-last" value={lastName} onChange={setLastName} />
            </div>
            <Field label="Work Email" id="reg-email" type="email" value={email} onChange={setEmail} />
            <Field label="Password" id="reg-password" type={showPwd ? 'text' : 'password'} value={password} onChange={setPassword} togglePwd={() => setShowPwd(!showPwd)} showPwd={showPwd} />
            <SubmitButton loading={loading} label="Create Account →" />
          </form>
        )}

        {mode === 'forgot' && (
          <form onSubmit={handleForgotPassword}>
            <Field label="Work Email" id="forgot-email" type="email" value={email} onChange={setEmail} />
            {forgotAwaitingCode && (
              <p style={styles.verifyHint}>
                If an account exists for this email, a code was sent. Enter it when you receive it.
              </p>
            )}
            <SubmitButton loading={loading} label={forgotAwaitingCode ? 'Resend Code →' : 'Send Reset Code →'} />
            {forgotAwaitingCode && (
              <button
                type="button"
                style={{ ...styles.linkBtn, display: 'block', width: '100%', marginTop: 8 }}
                onClick={() => { setMode('reset-password'); setError(''); }}
              >
                I have a code →
              </button>
            )}
          </form>
        )}

        {mode === 'reset-password' && (
          <form onSubmit={handleResetVerify}>
            <p style={styles.verifyHint}>Enter the code from your email for <strong>{email}</strong></p>
            <Field label="Reset Code" id="reset-code" value={resetOtp} onChange={setResetOtp} maxLength={8} />
            <Field
              label="New Password"
              id="new-pwd"
              type={showNewPwd ? 'text' : 'password'}
              value={newPassword}
              onChange={setNewPassword}
              togglePwd={() => setShowNewPwd((v) => !v)}
              showPwd={showNewPwd}
            />
            {/* Live password strength checklist */}
            {newPassword && (
              <ul style={styles.checklist}>
                {[
                  { label: '12–34 characters', ok: newPassword.length >= 12 && newPassword.length <= 34 },
                  { label: 'Uppercase letter (A-Z)', ok: /[A-Z]/.test(newPassword) },
                  { label: 'Lowercase letter (a-z)', ok: /[a-z]/.test(newPassword) },
                  { label: 'Number (0-9)', ok: /[0-9]/.test(newPassword) },
                  { label: 'Special character (@#$%&! etc.)', ok: /[^A-Za-z0-9]/.test(newPassword) },
                ].map(({ label, ok }) => (
                  <li key={label} style={{ ...styles.checklistItem, color: ok ? '#16a34a' : '#94a3b8' }}>
                    <span>{ok ? '✅' : '○'}</span> {label}
                  </li>
                ))}
              </ul>
            )}
            <Field
              label="Confirm New Password"
              id="confirm-pwd"
              type={showConfirmPwd ? 'text' : 'password'}
              value={confirmPassword}
              onChange={setConfirmPassword}
              togglePwd={() => setShowConfirmPwd((v) => !v)}
              showPwd={showConfirmPwd}
            />
            {newPassword && confirmPassword && newPassword !== confirmPassword && (
              <p style={styles.mismatchHint}>⚠ Passwords do not match</p>
            )}
            {newPassword && confirmPassword && newPassword === confirmPassword && (
              <p style={styles.matchHint}>✅ Passwords match</p>
            )}
            <SubmitButton loading={loading} label="Reset Password →" />
          </form>
        )}

        {mode === 'verify' && (
          <form onSubmit={handleVerifyOtp}>
            <Field label="Verification Code" id="verify-otp" value={otp} onChange={setOtp} />
            <SubmitButton loading={loading} label="Verify & Continue →" />
          </form>
        )}

        <div style={styles.footer}>
          {mode === 'login' && (
            <>
              <p>Don't have an account? <button type="button" style={styles.linkBtn} onClick={() => { setMode('register'); setError(''); }}>Register</button></p>
              <p><button type="button" style={styles.linkBtn} onClick={() => { setMode('forgot'); setError(''); }}>Forgot password?</button></p>
            </>
          )}
          {(mode === 'forgot' || mode === 'reset-password') && (
            <p>
              <button type="button" style={styles.linkBtn} onClick={() => { setMode('login'); setError(''); }}>← Back to sign in</button>
            </p>
          )}
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeIn { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
      `}</style>
    </div>
  );
}

function Field({ label, id, type = 'text', value, onChange, togglePwd, showPwd, maxLength }) {
  return (
    <div style={styles.formGroup}>
      <label style={styles.label} htmlFor={id}>{label}</label>
      <div style={styles.inputWrapper}>
        <input id={id} type={type} value={value} onChange={(e) => onChange(e.target.value)} required maxLength={maxLength} style={styles.input} />
        {togglePwd && <button type="button" onClick={togglePwd} style={styles.togglePwd}>{showPwd ? '🙈' : '👁'}</button>}
      </div>
    </div>
  );
}

function SubmitButton({ loading, label }) {
  return (
    <button type="submit" disabled={loading} style={{ ...styles.btn, ...(loading ? styles.btnLoading : {}) }}>
      {loading ? <span style={styles.spinner} /> : label}
    </button>
  );
}

const styles = {
  page: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    minHeight: '100vh',
    background: 'linear-gradient(165deg, #f8fafc 0%, #e2e8f0 45%, #f1f5f9 100%)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '24px', position: 'relative', overflow: 'hidden',
  },
  blob1: {
    position: 'absolute', top: '-80px', right: '-60px',
    width: '320px', height: '320px', borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(37,99,235,0.12) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  blob2: {
    position: 'absolute', bottom: '-100px', left: '-80px',
    width: '280px', height: '280px', borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(13,148,136,0.1) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  card: {
    position: 'relative', zIndex: 1,
    width: '100%', maxWidth: '420px',
    background: '#ffffff',
    border: '1px solid rgba(15, 23, 42, 0.08)',
    borderRadius: '16px',
    padding: '40px 36px',
    boxShadow: '0 20px 50px rgba(15, 23, 42, 0.1)',
    animation: 'fadeIn 0.4s ease',
  },
  header: { textAlign: 'center', marginBottom: '28px' },
  logo: { height: '44px', marginBottom: '16px' },
  title: { fontSize: '1.625rem', fontWeight: 700, color: '#0f172a', margin: '0 0 6px', letterSpacing: '-0.02em' },
  subtitle: { color: '#64748b', fontSize: '0.9375rem', margin: 0 },
  alert: {
    display: 'flex', alignItems: 'center', gap: '8px',
    padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca',
    borderRadius: '10px', color: '#b91c1c', fontSize: '0.875rem', marginBottom: '20px',
  },
  alertIcon: { fontSize: '1rem' },
  formGroup: { marginBottom: '20px' },
  nameRow: { display: 'flex', gap: '12px' },
  label: { display: 'block', fontSize: '0.8125rem', fontWeight: 600, color: '#334155', marginBottom: '6px' },
  inputWrapper: { position: 'relative', display: 'flex', alignItems: 'center' },
  input: {
    width: '100%', padding: '11px 14px',
    background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px',
    fontSize: '0.9375rem', color: '#0f172a', outline: 'none', boxSizing: 'border-box',
  },
  togglePwd: {
    position: 'absolute', right: '12px', background: 'none', border: 'none',
    cursor: 'pointer', fontSize: '16px', padding: '4px',
  },
  btn: {
    width: '100%', padding: '12px',
    background: 'linear-gradient(180deg, #2563eb 0%, #1d4ed8 100%)',
    color: '#fff', border: 'none', borderRadius: '8px',
    fontSize: '0.9375rem', fontWeight: 600, cursor: 'pointer', marginTop: '8px',
    boxShadow: '0 4px 14px rgba(37, 99, 235, 0.25)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  btnLoading: { opacity: 0.85, cursor: 'not-allowed' },
  spinner: {
    display: 'inline-block', width: '20px', height: '20px',
    border: '2px solid rgba(255,255,255,0.3)', borderTop: '2px solid #fff',
    borderRadius: '50%', animation: 'spin 0.7s linear infinite',
  },
  footer: { marginTop: '28px', textAlign: 'center', fontSize: '0.875rem', color: '#64748b' },
  linkBtn: {
    background: 'none', border: 'none', padding: 0, color: '#2563eb',
    fontWeight: 600, cursor: 'pointer', fontSize: 'inherit',
  },
  verifyHint: { fontSize: '0.875rem', color: '#64748b', marginBottom: '16px', textAlign: 'center' },
  mismatchHint: { fontSize: '0.8125rem', color: '#b91c1c', marginTop: '-12px', marginBottom: '12px' },
  matchHint: { fontSize: '0.8125rem', color: '#16a34a', marginTop: '-12px', marginBottom: '12px' },
  checklist: {
    listStyleType: 'none',
    padding: 0,
    margin: '8px 0 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  checklistItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '0.75rem',
  },
};
